import React from 'react';
import {
    Chip,
    Stack,
    Table,
    TableContainer,
    TableHead,
    TableBody,
    TableRow,
    TableCell,
    Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { formatCentsToUsd } from '../../../helpers/formatCentsToUsd';
import { getBookingStatusColor } from '../helpers/getBookingStatusColor';
import { useSort } from '../../../hooks/useSort';
import SortableHeaderCell from '../../../components/atom/SortableHeaderCell';

const GroupTable = ({
    group,
    showShowColumn,
    showGrandTotal,
    isLastGroup,
    totalGrossAmount,
    totalCommissionAmount,
    totalInvoiceAmount,
}) => {
    const { t } = useTranslation();
    const sort = useSort(group.items, 'bookingId', 'asc');

    return (
        <Stack key={`${group.commissionGroupId || 'unassigned'}-${group.showId}`}>
            <Typography variant="h6" sx={{ mb: 1 }}>
                {group.showTitle} - {group.commissionGroupName}
            </Typography>
            <TableContainer sx={{ maxHeight: '65vh' }}>
                <Table stickyHeader sx={{ width: '100%', '& thead th': { top: 0 } }}>
                    <TableHead>
                        <TableRow>
                            <SortableHeaderCell field="bookingId" sort={sort}>
                                {t('reports.columns.bookingId')}
                            </SortableHeaderCell>
                            <SortableHeaderCell field="status" sort={sort}>
                                {t('reports.columns.bookingStatus')}
                            </SortableHeaderCell>
                            {showShowColumn && (
                                <SortableHeaderCell field="showTitle" sort={sort}>
                                    {t('reports.columns.show', 'Show')}
                                </SortableHeaderCell>
                            )}
                            <SortableHeaderCell field="agentName" sort={sort}>
                                {t('reports.columns.agent')}
                            </SortableHeaderCell>
                            <SortableHeaderCell field="contact" sort={sort}>
                                {t('reports.columns.contact')}
                            </SortableHeaderCell>
                            <SortableHeaderCell field="customers" sort={sort}>
                                {t('reports.columns.customers')}
                            </SortableHeaderCell>
                            <SortableHeaderCell field="availability" sort={sort}>
                                {t('reports.affiliateInvoice.availability')}
                            </SortableHeaderCell>
                            <SortableHeaderCell field="bookingDate" sort={sort}>
                                {t('reports.affiliateInvoice.bookingDate')}
                            </SortableHeaderCell>
                            <SortableHeaderCell field="grossAmount" sort={sort} align="right">
                                {t('reports.affiliateInvoice.grossAmount')}
                            </SortableHeaderCell>
                            <SortableHeaderCell field="commissionAmount" sort={sort} align="right">
                                {t('reports.affiliateInvoice.commissionAmount')}
                            </SortableHeaderCell>
                            <SortableHeaderCell field="invoiceTotal" sort={sort} align="right">
                                {t('reports.affiliateInvoice.invoiceAmount', 'Invoice Amount')}
                            </SortableHeaderCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {sort.sortedItems.map((item) => (
                            <TableRow key={item.bookingId}>
                                <TableCell>{item.bookingId}</TableCell>
                                <TableCell>
                                    <Chip
                                        label={t(`bookingstatus.${item.status}`)}
                                        color={getBookingStatusColor(item.status)}
                                        size="small"
                                    />
                                </TableCell>
                                {showShowColumn && <TableCell>{item.showTitle}</TableCell>}
                                <TableCell>{item.agentName || ''}</TableCell>
                                <TableCell>
                                    {item.contact}
                                    <br />
                                    <Typography variant="caption" color="text.secondary">
                                        {item.email}
                                    </Typography>
                                </TableCell>
                                <TableCell>{item.customers}</TableCell>
                                <TableCell>{item.availability}</TableCell>
                                <TableCell>{item.bookingDate}</TableCell>
                                <TableCell align="right">
                                    {formatCentsToUsd(item.grossAmount)}
                                </TableCell>
                                <TableCell align="right">
                                    {formatCentsToUsd(item.commissionAmount)}
                                </TableCell>
                                <TableCell align="right">
                                    {formatCentsToUsd(item.invoiceTotal)}
                                </TableCell>
                            </TableRow>
                        ))}

                        <TableRow sx={{ backgroundColor: 'action.hover' }}>
                            <TableCell colSpan={showShowColumn ? 8 : 7}>
                                <strong>{t('reports.summary.groupTotal')}</strong>
                            </TableCell>
                            <TableCell align="right">
                                <strong>{formatCentsToUsd(group.grossAmount)}</strong>
                            </TableCell>
                            <TableCell align="right">
                                <strong>{formatCentsToUsd(group.commissionAmount)}</strong>
                            </TableCell>
                            <TableCell align="right">
                                <strong>{formatCentsToUsd(group.invoiceTotal)}</strong>
                            </TableCell>
                        </TableRow>

                        {showGrandTotal && isLastGroup && (
                            <TableRow sx={{ backgroundColor: 'action.selected' }}>
                                <TableCell colSpan={showShowColumn ? 8 : 7}>
                                    <strong>{t('reports.summary.grandTotal')}</strong>
                                </TableCell>
                                <TableCell align="right">
                                    <strong>{formatCentsToUsd(totalGrossAmount)}</strong>
                                </TableCell>
                                <TableCell align="right">
                                    <strong>{formatCentsToUsd(totalCommissionAmount)}</strong>
                                </TableCell>
                                <TableCell align="right">
                                    <strong>{formatCentsToUsd(totalInvoiceAmount)}</strong>
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
        </Stack>
    );
};

const AffiliateInvoiceReportTable = ({
    groups = [],
    showId,
    totalGrossAmount,
    totalCommissionAmount,
    totalInvoiceAmount,
}) => {
    const showShowColumn = !showId;
    const showGrandTotal = groups.length > 1;

    return (
        <Stack spacing={3} width="100%">
            {groups.map((group, index) => (
                <GroupTable
                    key={`${group.commissionGroupId || 'unassigned'}-${group.showId}`}
                    group={group}
                    showShowColumn={showShowColumn}
                    showGrandTotal={showGrandTotal}
                    isLastGroup={index === groups.length - 1}
                    totalGrossAmount={totalGrossAmount}
                    totalCommissionAmount={totalCommissionAmount}
                    totalInvoiceAmount={totalInvoiceAmount}
                />
            ))}
        </Stack>
    );
};

export default AffiliateInvoiceReportTable;
